import { photoUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { Send, Mail, Check, RotateCcw, Trash2, Loader2, Plus, ImagePlus, X, ChevronDown, History, UserCog, Bookmark, ArrowDownLeft, StickyNote, Clock, Palette } from "lucide-react";
import { slaChip, timeAgo, isImage } from "./utils";

export const ContactMessageCard = ({
  m, isAdmin, user, assignOptions, templates, snippets, teammateHint,
  isSelected, onToggleSelect,
  replyingId, replyText, setReplyText, attachments, uploading, sending,
  replyHandle, setReplyHandle, replyDeliver, setReplyDeliver,
  historyFor, history, noteFor, noteText, setNoteText,
  setHandled, remove, assign, passOn, openNote, addNote, deleteNote, closeNote,
  openReply, closeReply, setDesignStatus, insertTemplate, insertSnippet,
  onUpload, removeAttachment, sendReply, toggleHistory,
}) => {
  const lastReply = Array.isArray(m.replies) && m.replies.length ? m.replies[m.replies.length - 1] : null;
  const senderReplied = lastReply?.direction === "inbound";
  const notes = Array.isArray(m.notes) ? m.notes : [];
  const sla = slaChip(m);

  return (
    <div className={`rounded-xl border p-4 ${senderReplied ? "border-[#F5551E] bg-[#FFF9F6] ring-1 ring-[#F5551E]/20" : m.status === "new" ? "border-[#F5551E]/30 bg-[#FFF9F6]" : "border-slate-100"}`} data-testid={`contact-msg-${m.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-start gap-2.5">
          <Checkbox checked={isSelected} onCheckedChange={() => onToggleSelect(m.id)} data-testid={`contact-select-${m.id}`} className="mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold text-[#12234A] text-sm flex items-center gap-1.5 flex-wrap">
              {m.name}
              {senderReplied && <span data-testid={`contact-sender-replied-${m.id}`} className="text-[10px] font-bold bg-[#F5551E] text-white rounded-full px-1.5 py-0.5 flex items-center gap-0.5"><ArrowDownLeft size={10} /> Sender replied</span>}
              {!senderReplied && m.status === "new" && <span className="text-[10px] font-bold bg-[#F5551E] text-white rounded-full px-1.5 py-0.5">NEW</span>}
              {m.category === "design" && <span data-testid={`contact-design-badge-${m.id}`} className="text-[10px] font-bold bg-[#12234A] text-white rounded-full px-1.5 py-0.5 flex items-center gap-0.5"><Palette size={10} /> Design</span>}
              {sla && <span data-testid={`contact-sla-${m.id}`} className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 flex items-center gap-0.5 ${sla.tone}`}><Clock size={10} /> waiting {sla.label}</span>}
            </p>
            <a href={`mailto:${m.email}`} className="text-[12px] text-[#F5551E] hover:underline">{m.email}</a>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-[11px] text-slate-400 whitespace-nowrap">{timeAgo(m.created_at)}</span>
          {isAdmin ? (
            <Select value={m.assigned_to || "none"} onValueChange={(v) => assign(m, v)}>
              <SelectTrigger data-testid={`contact-assign-${m.id}`} className="h-7 rounded-lg text-[11px] w-[150px] gap-1"><UserCog size={12} className="text-slate-400" /><SelectValue placeholder="Assign" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid={`contact-assign-none-${m.id}`}>Unassigned</SelectItem>
                {assignOptions.filter((o) => o.user_id).map((o) => (
                  <SelectItem key={o.user_id} value={o.user_id} data-testid={`contact-assign-${o.user_id}-${m.id}`}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : m.assigned_to_name ? (
            <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 flex items-center gap-1" data-testid={`assignee-${m.id}`}><UserCog size={11} /> {m.assigned_to_name}{m.auto_assigned ? <span className="text-[9px] font-bold text-[#F5551E]">· auto</span> : null}</span>
          ) : null}
          {m.category === "design" && m.assigned_to && m.design_status !== "delivered" && (isAdmin || m.assigned_to === user?.user_id) && (
            <button onClick={() => passOn(m)} data-testid={`contact-pass-${m.id}`} className="text-[10px] font-semibold text-[#F5551E] hover:underline flex items-center gap-0.5"><RotateCcw size={11} /> Pass on</button>
          )}
        </div>
      </div>
      {m.subject && <p className="text-xs font-semibold text-slate-600 mt-2">Re: {m.subject}</p>}
      <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{m.message}</p>
      {Array.isArray(m.attachments) && m.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid={`contact-msg-attachments-${m.id}`}>
          {m.attachments.map((p) => (
            <a key={p} href={photoUrl(p)} target="_blank" rel="noreferrer"><img src={photoUrl(p)} alt="reference" className="h-16 w-16 rounded-lg object-cover border border-slate-200 hover:border-[#F5551E] transition-colors" /></a>
          ))}
        </div>
      )}

      {m.category === "design" && (() => {
        const ds = m.design_status || "new";
        const steps = [["new", "New"], ["in_design", "In design"], ["delivered", "Delivered"]];
        const fmtDur = (ms) => {
          const h = Math.floor(ms / 3600000);
          return h < 1 ? "<1h" : h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
        };
        let slaD = null;
        if (ds === "delivered" && m.design_delivered_at) {
          slaD = { label: `delivered in ${fmtDur(new Date(m.design_delivered_at) - new Date(m.created_at))}`, tone: "bg-emerald-100 text-emerald-700" };
        } else {
          const waitedMs = Date.now() - new Date(m.created_at).getTime();
          const h = waitedMs / 3600000;
          slaD = { label: `waiting ${fmtDur(waitedMs)}`, tone: h >= 48 ? "bg-red-100 text-red-700" : h >= 24 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500" };
        }
        return (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`design-status-bar-${m.id}`}>
            <span className="text-[11px] font-semibold text-slate-400 flex items-center gap-1"><Palette size={12} /> Design:</span>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              {steps.map(([v, l]) => (
                <button key={v} onClick={() => setDesignStatus(m, v)} data-testid={`design-status-${v}-${m.id}`}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors ${ds === v ? (v === "delivered" ? "bg-emerald-600 text-white" : "bg-[#12234A] text-white") : "text-slate-500 hover:text-[#12234A]"}`}>{l}</button>
              ))}
            </div>
            <span data-testid={`design-sla-${m.id}`} className={`text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-0.5 ${slaD.tone}`}><Clock size={10} /> {slaD.label}</span>
            {ds === "delivered" && m.design_delivered_by && <span className="text-[11px] text-emerald-600">by {m.design_delivered_by}</span>}
          </div>
        );
      })()}

      <button onClick={() => toggleHistory(m)} data-testid={`contact-history-toggle-${m.id}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-[#F5551E] transition-colors">
        <History size={12} /> Sender history <ChevronDown size={12} className={`transition-transform ${historyFor === m.id ? "rotate-180" : ""}`} />
      </button>
      {historyFor === m.id && (
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid={`contact-history-${m.id}`}>
          {!history ? <Skeleton className="h-10 rounded" /> : history.count === 0 ? (
            <p className="text-[12px] text-slate-400">No earlier messages from this sender.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold text-slate-500">{history.count} earlier message{history.count > 1 ? "s" : ""} from {m.email}</p>
              {history.messages.map((h) => (
                <div key={h.id} className="text-[12px] border-l-2 border-slate-200 pl-2">
                  <p className="text-slate-400">{h.subject ? `Re: ${h.subject}` : "No subject"} · {timeAgo(h.created_at)} {h.status === "handled" && <span className="text-emerald-600">· handled</span>}</p>
                  <p className="text-slate-600 line-clamp-2">{h.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {Array.isArray(m.replies) && m.replies.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`contact-replies-${m.id}`}>
          {m.replies.map((rep) => {
            const inbound = rep.direction === "inbound";
            return (
              <div key={rep.id} className={`rounded-lg border p-3 ${inbound ? "bg-slate-50 border-slate-200 ml-6" : "bg-emerald-50 border-emerald-100 mr-6"}`} data-testid={`contact-reply-${inbound ? "inbound" : "outbound"}-${rep.id}`}>
                <p className={`text-[11px] font-semibold flex items-center gap-1 ${inbound ? "text-slate-500" : "text-emerald-700"}`}>
                  {inbound ? <ArrowDownLeft size={11} /> : <Send size={11} />} {inbound ? `${rep.by} replied` : `Replied by ${rep.by}`} · {timeAgo(rep.at)}
                </p>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{rep.body}</p>
                {Array.isArray(rep.attachments) && rep.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {rep.attachments.map((p) => (
                      isImage(p)
                        ? <a key={p} href={photoUrl(p)} target="_blank" rel="noreferrer"><img src={photoUrl(p)} alt="attachment" className="h-14 w-14 rounded-lg object-cover border border-emerald-200" /></a>
                        : <a key={p} href={photoUrl(p)} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-700 underline">📎 File</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {replyingId === m.id ? (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`contact-composer-${m.id}`}>
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <span className="text-[11px] font-semibold text-slate-400 mr-1">Insert template:</span>
            {templates.map((tpl) => (
              <Button key={tpl.id} size="sm" variant="outline" onClick={() => insertTemplate(m, tpl)} data-testid={`contact-template-${tpl.id}-${m.id}`} className="rounded-lg h-7 text-xs gap-1"><Mail size={12} /> {tpl.label}</Button>
            ))}
          </div>
          {snippets.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid={`contact-snippets-${m.id}`}>
              <span className="text-[11px] font-semibold text-slate-400 mr-1">My snippets:</span>
              {snippets.map((snip) => (
                <Button key={snip.id} size="sm" variant="outline" onClick={() => insertSnippet(m, snip)} data-testid={`contact-snippet-${snip.id}-${m.id}`} className="rounded-lg h-7 text-xs gap-1 border-amber-200 text-amber-700 hover:bg-amber-50"><Bookmark size={12} /> {snip.label}</Button>
              ))}
            </div>
          )}
          <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={`Write a reply to ${m.name}…`} data-testid={`contact-reply-body-${m.id}`} className="rounded-lg min-h-[110px] text-sm" maxLength={4000} />
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid={`contact-attachments-${m.id}`}>
              {attachments.map((p) => (
                <div key={p} className="relative group">
                  {isImage(p)
                    ? <img src={photoUrl(p)} alt="attachment" className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                    : <div className="h-16 w-16 rounded-lg border border-slate-200 flex items-center justify-center text-[10px] text-slate-500">📎 File</div>}
                  <button onClick={() => removeAttachment(p)} data-testid={`contact-attachment-remove-${m.id}`} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center shadow"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Button size="sm" onClick={() => sendReply(m)} disabled={sending || uploading} data-testid={`contact-reply-send-${m.id}`} className="rounded-lg h-8 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send reply
            </Button>
            <label className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#F5551E] cursor-pointer" data-testid={`contact-attach-label-${m.id}`}>
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Attach
              <input type="file" accept="image/*,.pdf" multiple className="hidden" onChange={onUpload} data-testid={`contact-attach-input-${m.id}`} />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
              <Switch checked={replyHandle} onCheckedChange={setReplyHandle} data-testid={`contact-reply-handle-${m.id}`} /> Mark handled after sending
            </label>
            {m.category === "design" && (
              <label className="flex items-center gap-1.5 text-xs text-[#12234A] font-semibold whitespace-nowrap">
                <Switch checked={replyDeliver} onCheckedChange={setReplyDeliver} data-testid={`contact-reply-deliver-${m.id}`} /> <Palette size={12} /> Mark banner delivered
              </label>
            )}
            <Button size="sm" variant="ghost" onClick={closeReply} data-testid={`contact-reply-cancel-${m.id}`} className="rounded-lg h-8 text-slate-500">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">
          <Button size="sm" onClick={() => openReply(m)} data-testid={`contact-reply-open-${m.id}`} className="rounded-lg h-8 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1"><Send size={14} /> Reply</Button>
          {m.status === "new" ? (
            <Button size="sm" variant="outline" onClick={() => setHandled(m, true)} data-testid={`contact-handle-${m.id}`} className="rounded-lg h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50 gap-1"><Check size={14} /> Mark handled</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setHandled(m, false)} data-testid={`contact-reopen-${m.id}`} className="rounded-lg h-8 gap-1"><RotateCcw size={14} /> Reopen</Button>
          )}
          {m.status === "handled" && m.handled_by && <span className="text-[11px] text-slate-400">Handled by {m.handled_by}</span>}
          <button onClick={() => remove(m.id)} data-testid={`contact-delete-${m.id}`} className="ml-auto text-red-500 hover:text-red-600"><Trash2 size={15} /></button>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-dashed border-amber-200/70">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold text-amber-700 flex items-center gap-1"><StickyNote size={12} /> Internal notes <span className="font-normal text-amber-600/70">· staff-only, never emailed</span></p>
          {noteFor !== m.id && <button onClick={() => openNote(m)} data-testid={`contact-note-add-${m.id}`} className="text-[11px] font-semibold text-amber-700 hover:text-amber-800 flex items-center gap-0.5"><Plus size={12} /> Add note</button>}
        </div>
        {notes.length > 0 && (
          <div className="mt-2 space-y-1.5" data-testid={`contact-notes-${m.id}`}>
            {notes.map((n) => (
              <div key={n.id} className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-amber-700">{n.by} · {timeAgo(n.at)}</p>
                  <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{n.body}</p>
                </div>
                <button onClick={() => deleteNote(m, n.id)} data-testid={`contact-note-delete-${n.id}`} className="text-amber-500 hover:text-red-500 shrink-0"><X size={13} /></button>
              </div>
            ))}
          </div>
        )}
        {noteFor === m.id && (
          <div className="mt-2" data-testid={`contact-note-composer-${m.id}`}>
            <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a private note for the team…" data-testid={`contact-note-input-${m.id}`} className="rounded-lg min-h-[70px] text-sm bg-amber-50/40 border-amber-200" maxLength={2000} />
            <div className="flex items-center gap-2 mt-1.5">
              <Button size="sm" onClick={() => addNote(m)} data-testid={`contact-note-save-${m.id}`} className="rounded-lg h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white gap-1"><Check size={13} /> Save note</Button>
              <Button size="sm" variant="ghost" onClick={closeNote} data-testid={`contact-note-cancel-${m.id}`} className="rounded-lg h-7 text-xs text-slate-500">Cancel</Button>
            </div>
            {teammateHint && <p className="text-[10px] text-slate-400 mt-1">Tip: mention a teammate to ping them — {teammateHint}</p>}
          </div>
        )}
      </div>
    </div>
  );
};
